import { Box, Chip, IconButton } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { AsyncMultiAutocomplete, Option } from '../../../common/AsyncMultiAutocomplete';
import EditIcon from '../../../common/icons/EditIcon';
import { ApplicantProfileData } from '../../../../pages/rec/applicant/ApplicantProfile';
import api from '../../../../services/httpClient';
import { SETTINGS_ENDPOINTS } from '../../../../services/endpoints/settings.endpoints';
import { SYSTEM_ENDPOINTS } from '../../../../services/endpoints/system.endpoints';
import { usePermissions } from '../../../../context/PermissionContext';
import { API_HEADERS } from '../../../../constants/apiHeaders';
import { PERMISSION_IDS } from '../../../../constants/permissions';

interface SkillItem {
  name: string;
}

interface ApplicantProfileSkillCardProps {
  isEditMode: boolean;
  setIsEditMode?: (isEditMode: boolean) => void;
  values: ApplicantProfileData;
  setValues: (values: ApplicantProfileData) => void;
}

const SkillChip: React.FC<SkillItem> = ({ name }) => (
  <Chip
    label={name}
    title={name}
    className="view-tag-title"
  />
);

const ApplicantProfileSkillCard: React.FC<ApplicantProfileSkillCardProps> = ({
  values,
  setValues,
  isEditMode,
  setIsEditMode
}) => {
  const [isEdit, setIsEdit] = useState(false);
  const { hasPermission } = usePermissions();
  
  useEffect(() => {
    if (isEditMode === false) {
      setIsEdit(false);
    }
  }, [isEditMode]);

  const handleLoadSkills = async (query: string) => {
    const response = await api.get<{ data: { id: number; name: string }[] }>(
      SETTINGS_ENDPOINTS.skills,
      {
        headers: { ...API_HEADERS.SETTINGS_SKILLS_VACANCY_ADD },
      }
    );
    return response.data.data.map((item) => ({
      value: item.id.toString(),
      label: item.name,
    }));
  };

  const handleLoadTechnologies = async (query: string) => {
    const response = await api.get<{ data: { id: number; name: string }[] }>(
      `${SETTINGS_ENDPOINTS.technologies}?query=${encodeURIComponent(query)}`,
      {
        headers: { ...API_HEADERS.VACANCY_SKILL_TECHNOLOGIES_GET },
      }
    );
    return response.data.data.map((item) => ({
      value: item.id.toString(),
      label: item.name,
    }));
  };

  const handleLoadLanguages = async (query: string) => {
    const response = await api.get<{ data: { id: number; name: string }[] }>(
      SYSTEM_ENDPOINTS.languages,
      {
        headers: { ...API_HEADERS.APPLICANT_PROFILE_LANGUAGES_LOOKUP },
      }
    );
    return response.data.data.map((item) => ({
      value: item.id.toString(),
      label: item.name,
    }));
  };

  return (
    <Box className="view-section">
      <Box sx={{ position: 'relative' }}>
        <Box sx={{ position: 'absolute', top: '-12px', right: '-12px' }}>
          {!isEdit && hasPermission(PERMISSION_IDS.APPLICANT_PROFILE_EDIT) && (
            <IconButton
              onClick={() => {
                setIsEdit(true);
                setIsEditMode?.(true);
              }}
              className="view-edit-icon"
            >
              <EditIcon />
            </IconButton>
          )}
        </Box>
        <Box className="view-row-box">
          <Box className="view-column-box">
            <div className="view-label-title">
              Skills
            </div>
            {!isEdit && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {values.skills?.length > 0 ? values.skills.map((item, itemIndex) => (
                  <SkillChip key={itemIndex} {...item} />
                )) : <div className="view-value-title">N/A</div>}
              </Box>
            )}
            {isEdit && (
              <AsyncMultiAutocomplete
                multiple
                className="view-column-edit-box"
                value={values.skills.map((item) => ({ value: item.id.toString(), label: item.name })) || []}
                onChange={(value: Option | Option[] | null) => {
                  setValues({ ...values, skills: value ? (Array.isArray(value) ? value.map((item) => ({ id: Number(item.value), name: item.label })) : [{ id: Number(value.value), name: value.label }]) : [] });
                }}
                loadOptions={handleLoadSkills}
              />
            )}
          </Box>
          <Box className="view-column-box">
            <div className="view-label-title">
              Technologies
            </div>
            {!isEdit && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {values.technologies?.length > 0 ? values.technologies.map((item, itemIndex) => (
                  <SkillChip key={itemIndex} {...item} />
                )) : <div className="view-value-title">N/A</div>}
              </Box>
            )}
            {isEdit && (
              <AsyncMultiAutocomplete
                multiple
                className="view-column-edit-box"
                value={values.technologies.map((item) => ({ value: item.id.toString(), label: item.name }))}
                onChange={(value: Option | Option[] | null) => {
                  setValues({ ...values, technologies: value ? (Array.isArray(value) ? value.map((item) => ({ id: Number(item.value), name: item.label })) : [{ id: Number(value.value), name: value.label }]) : [] });
                }}
                loadOptions={handleLoadTechnologies}
              />
            )}
          </Box>
          <Box className="view-column-box">
            <div className="view-label-title">
              Languages
            </div>
            {!isEdit && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {values.languages?.length > 0 ? values.languages.map((item, itemIndex) => (
                  <SkillChip key={itemIndex} {...item} />
                )) : <div className="view-value-title">N/A</div>}
              </Box>
            )}
            {isEdit && (
              <AsyncMultiAutocomplete
                multiple
                className="view-column-edit-box"
                value={values.languages.map((item) => ({ value: item.id.toString(), label: item.name }))}
                onChange={(value: Option | Option[] | null) => {
                  setValues({ ...values, languages: value ? (Array.isArray(value) ? value.map((item) => ({ id: Number(item.value), name: item.label })) : [{ id: Number(value.value), name: value.label }]) : [] });
                }}
                loadOptions={handleLoadLanguages}
              />
            )}
          </Box>
          {/* <Box className="view-column-box" /> */}
        </Box>
      </Box>
    </Box>
  );
};

export default ApplicantProfileSkillCard; 